import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Container, Row } from '@geberit/gdds';
import { useSearchParams } from 'next/navigation';

// styles
import styles from './showroom.module.scss';

// types
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import type { Category, SidebarProps } from './sidebar';
import type { FilterProps } from './filter';
import type { Coordinates } from './showroom.types';

// components
import { useIsMobile } from 'components/App/SizeProvider';
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { SectionClosingLine } from 'components/ContentElementsGdds/Common/section-closing-line';
import { LocatorConsent, useUCConsent } from 'features/cookie-consent';
import { Sidebar } from './sidebar';
import { Filter } from './filter';
import { GoogleMap } from './google-map';
import { MapLegend } from './map-legend';
import { useMarkerCluster } from './marker-cluster';
import { LocatorLightbox } from './sidebar/detail/locator-lightbox';

// utils
import { Log } from 'services/log';
import { getGoogle } from 'utils/getGoogle';
import { useGeoLocation } from 'utils/hooks/useGeoLocation';
import { filterData, preprocessData } from 'utils/locator';
import { useTracking } from 'utils/hooks/useTracking';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useKolo } from 'utils/hooks/use-kolo';
import { useXy } from 'utils/hooks/use-xy';
import { useIsPreview } from 'utils/hooks/useIsPreview';
import { useCurrentLanguage } from 'utils/hooks/useCurrentLanguage';
import { globalsServerSelector } from 'utils/selectors/globalsSelectors';
import { useSectionId } from 'utils/hooks/use-section-id';
import { isEmpty } from 'utils/is-empty';
import { geocode } from 'utils/get-coordinates';
import { buildSize, gridSizes } from 'utils/gridSize';
import { locatorEvent } from './tracking-actions';
import { getPartnerData } from './actions';
import { locatorConfig } from './constants';
import { getDefaultMarkerColor, getMarkerColors } from './utils';

export function ShowroomLocator(props: Readonly<Locator>) {
  const {
    anchor = '',
    autoCoordinates,
    border,
    contentIndex,
    data,
    dataMode,
    detailLayer,
    hideFilters = false,
    productCategoryLabel = '',
    searchPlaceholder = '',
    seriesLabel = '',
    showroom,
    startCoordinates,
    title = '',
    subtitle = '',
    titleInAnchor = false,
    zoom,
  } = props;
  const searchParams = useSearchParams();
  const isPreview = useIsPreview();
  const language = useCurrentLanguage();
  const isMobile = useIsMobile();
  const mapZoom = Number(zoom) || 10;
  const googleMapsRef = useRef<typeof google.maps | null>(null);
  const mapRef = useRef(null);
  const [gmap, setGmap] = useState<google.maps.Map | null>(null);
  const server = useSelector(globalsServerSelector);
  const [, setGoogleState] = useState<typeof window.google.maps | null>(null);
  const ucConsent = useUCConsent();
  const track = useTracking();
  const isKolo = useKolo();
  const isXy = useXy();
  const [hasError, setHasError] = useState(false);
  const [categories, setCategories] = useState<SidebarProps['categories']>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userCoords] = useGeoLocation();
  const [geocoded, setGeocoded] = useState(false);
  const [lat, lng] = startCoordinates
    ? startCoordinates.split(',').map(parseFloat)
    : [undefined, undefined];
  const [fullData, setFullData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [maxDistance, setMaxDistance] = useState<FilterProps['maxDistance']>('none');
  const [filterSerie, setFilterSerie] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterModel, setFilterModel] = useState<string[]>([]);
  const [currentCoords, setCurrentCoords] = useState<Coordinates | undefined>(
    lng && lat
      ? {
          lat,
          lng,
        }
      : undefined,
  );
  const [isSelectedDropdown, setIsSelectedDropdown] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const sectionId = useSectionId(title, anchor, titleInAnchor, contentIndex);
  const [sortedF1Categories, setSortedF1Categories] = useState(null);
  const fallbackLanguage = language ?? '';
  const countryCode = language.includes('_') ? language.split('_')[1] : fallbackLanguage;

  const handleDetailToggle = useCallback(
    (item, isAccView) => {
      // check if it is an synthetic event or not
      // synthethic event means it comes from the lightbox
      const newItem = item && !item.target ? item : null;
      const detailShouldOpen = !isAccView && Boolean(newItem);

      setDetailItem(newItem);
      setDetailOpen(detailShouldOpen);

      if (newItem) {
        track.trackEvent(locatorEvent('Locator-Details', item.title || item.name));
      }
    },
    [track],
  );

  useEffect(() => {
    // user has enabled geolocation and not provided an address
    if (!isEmpty(userCoords) && isEmpty(currentCoords)) {
      setCurrentCoords(userCoords);
    }
  }, [currentCoords, userCoords]);

  useEffect(() => {
    let isMounted = true;
    googleMapsRef.current = window.google?.maps;
    const canGoogleBeLoaded = ucConsent.GoogleMaps === true;
    if (canGoogleBeLoaded && !googleMapsRef.current) {
      getGoogle(locatorConfig.map, server?.googleMapsApiKey)
        .then(() => {
          googleMapsRef.current = window.google.maps;
          setGoogleState(googleMapsRef.current);
        })
        .catch((e) => Log.error(e));
    }

    const markerColor = getMarkerColors({ isXy, isKolo });
    const markerColorDefault = getDefaultMarkerColor({ isXy, isKolo });

    if (dataMode !== 'showroom' && data) {
      getPartnerData({
        isPreview,
        url: data,
      })
        .then((response) => {
          if (isMounted) {
            const { processedData, colorCodedCategories } = preprocessData(
              dataMode,
              response,
              markerColor,
              markerColorDefault,
              language,
            );
            setFullData(processedData);
            setFilteredData(processedData);
            setCategories(colorCodedCategories ?? []);
            setHasError(false);
          }
        })
        .catch(() => {
          if (isMounted) {
            setHasError(true);
          }
        });
    } else {
      const { processedData, colorCodedCategories } = preprocessData(
        dataMode,
        {
          data: showroom,
        },
        markerColor,
        markerColorDefault,
        null,
      );
      setFullData(processedData);
      setCategories(colorCodedCategories ?? []);
      setFilteredData(processedData);
      setSortedF1Categories(showroom?.[0]?.sortStructure);
    }

    return () => {
      isMounted = false;
    };
  }, [data, dataMode, isKolo, isPreview, language, server?.googleMapsApiKey, showroom, ucConsent]);

  useEffect(() => {
    if (ucConsent.GoogleMaps === false) {
      setGmap(null);
    }
  }, [ucConsent.GoogleMaps]);

  useEffect(() => {
    if (googleMapsRef.current) {
      const payload = {
        fullDataset: fullData,
        coordinates: currentCoords,
        mode: dataMode,
        maxDistance,
        filterSerie,
        filterCategory,
        filterModel,
      };

      const newFilteredData = filterData(googleMapsRef.current, payload, language);
      setFilteredData(newFilteredData);
      setSidebarOpen(true);
    }
  }, [
    maxDistance,
    currentCoords,
    filterCategory,
    filterSerie,
    filterModel.length,
    autoCoordinates,
    fullData,
    dataMode,
    filterModel,
    language,
  ]);

  useEffect(() => {
    async function getGeocode(location) {
      const geoLocation = await geocode(location);
      return geoLocation;
    }

    const location = searchParams.get('location');

    if (location && googleMapsRef.current) {
      getGeocode(location).then((response) => {
        setCurrentCoords(response?.coordinates);
      });
    }
  }, [countryCode, googleMapsRef.current]);

  useMarkerCluster({
    isNord: true,
    filteredData,
    mode: dataMode,
    categories,
    mapRef: gmap,
    googleMapsRef: googleMapsRef.current,
    handleDetail: handleDetailToggle,
    startLat: lat,
    startLng: lng,
    autoCoordinates,
  });

  if (hasError) {
    return null;
  }

  return (
    <div id={sectionId} className={styles.wrapper}>
      <Container
        className={styles.locatorNord}
        id={sectionId}
        maxContentWidth={buildSize(gridSizes.gddsFullGrid)}
      >
        <Headline
          className="title"
          title={title}
          subtitle={subtitle}
          tag={Formats.h2}
          titlePreviewId="#st_title"
          subtitlePreviewId="#st_subtitle"
        />
      </Container>
      {ucConsent.GoogleMaps === true && (
        <>
          <div className={classNameBuilder(styles.locatorWrapper, styles.nord)}>
            <GoogleMap
              zoom={
                maxDistance === 'none'
                  ? mapZoom
                  : Math.floor(Math.log2(40000 / (Number(maxDistance) / 1000)))
              }
              coordinates={currentCoords as google.maps.LatLngLiteral}
              googleMapsRef={googleMapsRef.current}
              ref={mapRef}
              gmap={gmap}
              setGmap={setGmap}
              mapConfig={locatorConfig.map}
            />

            <Filter
              config={props}
              googleMapsRef={googleMapsRef.current}
              countryCode={countryCode}
              currentCoords={currentCoords}
              mode={dataMode}
              searchPlaceholder={searchPlaceholder}
              setMaxDistance={setMaxDistance}
              maxDistance={maxDistance}
              setIsSelectedDropdown={setIsSelectedDropdown}
              isSelectedDropdown={isSelectedDropdown}
              setCurrentCoords={setCurrentCoords}
              setGeocoded={setGeocoded}
              fullData={fullData}
              filterSerie={filterSerie}
              setFilterSerie={setFilterSerie}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              filterModel={filterModel}
              setFilterModel={setFilterModel}
              productCategoryLabel={productCategoryLabel}
              seriesLabel={seriesLabel}
              hideFilters={hideFilters}
              sortedF1Categories={sortedF1Categories}
              isMobile={isMobile}
            />
            <Sidebar
              categories={categories}
              filteredData={filteredData}
              sidebarOpen={sidebarOpen}
              showDistance={!isEmpty(userCoords) || geocoded === true || !isEmpty(currentCoords)}
              setSidebarOpen={setSidebarOpen}
              handleDetail={handleDetailToggle}
              mode={dataMode}
            />
          </div>
          <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)}>
            <Row>
              <Col>
                {detailOpen && detailItem && (
                  <LocatorLightbox
                    item={detailItem}
                    detailLayer={detailLayer}
                    onClose={handleDetailToggle}
                    isNord
                    locator={dataMode}
                  />
                )}

                {dataMode !== 'hotel' && (
                  <MapLegend isNord categories={categories as unknown as Category[]} />
                )}
              </Col>
            </Row>
          </Container>
        </>
      )}
      {ucConsent.GoogleMaps === false && <LocatorConsent />}
      {!border && <SectionClosingLine withBottomMargin />}
    </div>
  );
}
